// @flow
import Spinner from "components/Spinner";
import { useLoadPackets } from "queries/packets/useLoadPackets";
import * as React from "react";
import { toast } from "react-toastify";
import Flex from "ui/atoms/Flex";
import FlexBox from "ui/atoms/FlexBox";
import { isFilledArray } from "utils/helpers";
import { ZentakeContext } from "utils/ZentakeContext";

import zentakeApi from "@/api";
import { DATE_FORMATS, getFormattedDate } from "@/utils/dates/getFormattedDate";

import useSelectPagination from "./hooks/useSelectPagination";
import SendResourceModal from "./SendResourceModal";

type Props = {
  onHide: () => void,
};

function SendPacket({ onHide }: Props): React.Node {
  const { userDetails, reloadMessages } = React.useContext(ZentakeContext);
  const [hasMore, setHasMore] = React.useState(false);

  const { params, onInputChange, onMenuScrollToBottom, setOptions, options } =
    useSelectPagination(hasMore);

  const { data, isLoading, isFetching, refetch } = useLoadPackets(params, {
    staleTime: Infinity,
    keepPreviousData: true,
  });

  React.useEffect(() => {
    setHasMore(Boolean(data?.next));
  }, [data?.next]);

  const packets = React.useMemo(
    () => (data?.results || []).filter((p) => p.enabled),
    [data],
  );

  const hasPackets = isFilledArray(packets);

  const renderDropdownLabel = React.useCallback(
    () => (
      <Flex>
        <FlexBox>
          <span>Select Packet</span>
        </FlexBox>
        {isLoading && <Spinner color="onLight" size={20} />}
      </Flex>
    ),
    [isLoading],
  );

  const buildOptions = React.useCallback(
    (data) => {
      const newOptions = packets
        .map((o) => ({
          value: o.id,
          label: o.name,
          hasOfficeUseQuestions: o.office_use_forms_count > 0,
        }))
        .sort((a, b) => a.label?.localeCompare(b.label));

      setOptions((options) => [...options, ...newOptions]);
    },
    [packets, setOptions],
  );

  React.useEffect(() => {
    buildOptions(data);
  }, [buildOptions, data]);

  const dropdownPlaceholder = hasPackets
    ? "Select Packet"
    : "No Packet Available";

  const doSendPacket = React.useCallback(
    async ({
      personToUse,
      is_waiting,
      email,
      id,
      messages,
      setMessages,
      teamDetails,
      phone,
      willUseEmail,
      scheduledDate,
    }) => {
      if (!userDetails?.is_verified) {
        toast.warning("Verify Email");
        return;
      }

      try {
        const apiResult = await zentakeApi.sendMessageToCustomer({
          targetEmail: email,
          customer: personToUse?.id,
          preferredDeliveryMethod: willUseEmail ? "email" : "sms",
          deliveryMethodUsed: willUseEmail ? "email" : "sms",
          messageType: "packet_response_request",
          formId: null,
          packetId: id,
          is_waiting,
          team: teamDetails?.uid,
          phoneNumber: phone,
          scheduledDate: getFormattedDate(scheduledDate, {
            format: DATE_FORMATS.ISO_DATETIME,
            utc: true,
          }),
        });
        !is_waiting && toast.info("Packet Sent");
        reloadMessages();
        return apiResult;
      } catch (error) {
        // do nothing
      }
    },
    [reloadMessages, userDetails?.is_verified],
  );

  async function afterSelect() {
    onInputChange("");
    const { data } = await refetch();
    buildOptions(data);
  }

  return (
    <SendResourceModal
      isPacket
      showScheduleSendButton
      afterSelect={afterSelect}
      dropdownDisabled={isLoading}
      dropdownLabel={renderDropdownLabel()}
      dropdownPlaceholder={dropdownPlaceholder}
      heading="Send Packet"
      isFetching={isFetching}
      isVerified={userDetails?.is_verified || false}
      loading={isLoading}
      onHide={onHide}
      onMenuScrollToBottom={onMenuScrollToBottom}
      onType={onInputChange}
      options={options}
      sendResource={doSendPacket}
      subheading="Complete the following to send a packet."
    />
  );
}
export default SendPacket;
